import React from "react";
import EventTargetLocal from "../shared/EventTarget";

export const event = new EventTargetLocal();

const store: Record<
  string,
  {
    value?: any;
    status: 0 | 1 | -1;
    error?: Error;
  }
> = {};

const listenerCount: Record<string, number> = {};

const callbackMap = new WeakMap();

const useStore = <T>(
  key: string,
  callback: () => Promise<any>,
  isLogin: boolean
): {
  data?: T;
  error?: any;
  loading: boolean;
} => {
  const [data, setData] = React.useState(() => {
    const cache = store[key];
    if (cache) {
      return {
        data: cache.value,
        loading: cache.status === 0,
        error: cache.error,
      };
    }
    return {
      loading: true,
    };
  });
  React.useEffect(() => {
    if (!isLogin) {
      return;
    }
    if (!callbackMap.has(callback)) {
      callbackMap.set(callback, (option?: { force?: boolean }) => {
        const force = option && option.force;
        if (!force) {
          if (store[key] && store[key].status === 0) {
            return;
          }
        }
        callback()
          .then((data) => {
            store[key] = {
              value: data,
              status: 1,
            };
          })
          .catch((error: Error) => {
            store[key] = {
              error,
              status: -1,
            };
          })
          .finally(() => {
            event.dispatchEvent(new CustomEvent(key));
          });
      });
    }
    const requestHandle = callbackMap.get(callback);
    const handleEvent = () => {
      setData({
        data: store[key].value,
        loading: false,
        error: store[key].error,
      });
    };
    const destroy = () => {
      listenerCount[key] -= 1;
      event.removeEventListener(key, handleEvent);
      if (listenerCount[key] === 0) {
        event.removeEventListener(key + ":update", requestHandle);
      }
    };
    if (listenerCount[key]) {
      listenerCount[key] += 1;
      event.addEventListener(key, handleEvent);
      return destroy;
    }
    if (listenerCount[key] === undefined) {
      listenerCount[key] = 0;
    }
    listenerCount[key] += 1;
    if (!store[key]) {
      store[key] = {
        status: 0,
      };
      requestHandle({ force: true });
    }
    event.addEventListener(key, handleEvent);
    event.addEventListener(key + ":update", requestHandle);
    return destroy;
  }, [key, callback, isLogin]);
  return data;
};

export default useStore;
